import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useMobileVerificationMutation } from '../../store/auth/usersApiSlice';
import { ReactComponent as RightArrow } from "../../assets/icons/right-arrow.svg";
import { ReactComponent as VerifyIcon } from "../../assets/icons/verifyIcon.svg";
import { ReactComponent as Spinner } from "../../assets/icons/spinner.svg";
import { CommonToast } from './Toast';
import CountrySelect from '../CountrySelect';
import './PhoneNumberInput.css';
import useCountryCode from '../../Hooks/useCountryCode';

const PhoneNumberInput = forwardRef(({ phone, setPhone=()=>{}, showVerified = false, setShowVerified = () => { }, returnPhoneNumber = false, verifyPhoneNumber = true, error = "", onChange, border=false, classes, phoneRef, readOnly= false, countrySelect, defaultCountry }) => {
    const inputRefs = useRef([]);
    const { countryCode } = useCountryCode();
    const [isValid, setIsValid] = useState(false);
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [isOTPValid, setIsOTPValid] = useState(false);
    const [encryptedKey, setEncryptedKey] = useState("");
    const [mobileVerification] = useMobileVerificationMutation();
    const [loading, setLoading] = useState(false);

    useImperativeHandle(phoneRef, () => ({
        focus: () => {
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }
        }
    }));

    const handlePhoneChange = (value) => {
        setPhone(value);
        if (value) {
            setIsValid(isValidPhoneNumber(value));
        } else {
            setIsValid(false);
        }
        if (onChange) {
            onChange(value);
        }
    };

    const handleVerifyPhoneNumber = async (otpValidation = false) => {
        setLoading(true);
        const data = {
            mobile: phone,
            ...(encryptedKey && { encrypted: encryptedKey, code: otp?.join('') })
        };
        const MobileVerificationResponse = await mobileVerification(data);
        if (MobileVerificationResponse?.data) {
            if (otpValidation && MobileVerificationResponse?.data?.status !== "OTP_INVALID") {
                setShowVerified(true);
                setEncryptedKey("");
            } else if (!otpValidation) {
                setEncryptedKey(MobileVerificationResponse?.data?.encrypted);
            }
            if (MobileVerificationResponse?.data?.status === "OTP_INVALID") {
                setOtp(["", "", "", "", "", ""]);
            }
            CommonToast.info(MobileVerificationResponse?.data?.message);
        }
        setLoading(false);
    };

    const handleChange = (e, index) => {
        const inputValue = e.target.value?.replace(/[^0-9]/g, '');
        const newOtp = [...otp];
        newOtp[index] = inputValue;
        setOtp(newOtp);
        if (inputValue !== "" && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && otp[index] === "" && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const newOtp = pastedText
            .split('')
            .slice(0, otp.length)
            .map((char) => char);
        setOtp(newOtp);
    };

    useEffect(() => {
        const allFilled = otp.every(num => /^[0-9]$/.test(num));
        setIsOTPValid(allFilled);
    }, [otp]);

    /* `border` promised a bordered field and never drew one: the classes were `bg-input focus:border-primary rounded` - a background and a focus COLOUR, with no... */
    return (
        <div data-testid="phone-field" className={`phone-field relative ${classes === 'addBackground' ? "addBackground" : ""} ${border ? "border border-input bg-white rounded focus-within:border-primary" : "border-b border-primary pt-3"}`}>
            <PhoneInput
                international
                /* `defaultCountry` lets a caller fix the initial flag. */
                defaultCountry={defaultCountry || countryCode}
                countryCallingCodeEditable={false}
                value={phone}
                onChange={handlePhoneChange}
                /* `countrySelect` lets a caller supply its own flag picker.
                   Omitted — as every existing caller omits it — this is the
                   picker it has always been. */
                countrySelectComponent={
                  countrySelect ||
                  ((props) => <CountrySelect {...props} classes={classes} readOnly={readOnly} />)
                }
                disabled={showVerified}
                classes={classes}
                className={classes}
                readOnly={readOnly}
            />
            {(verifyPhoneNumber && isValid && !encryptedKey && !isOTPValid && !showVerified) && (
                <div onClick={loading ? null : () => handleVerifyPhoneNumber(false)} className={`absolute top-[20px] right-0 text-link font-bold ${isValid ? "cursor-pointer" : "cursor-not-allowed"}`}>
                    {loading ? "Sending OTP" : "Verify"}
                </div>
            )}
            {verifyPhoneNumber && encryptedKey && !showVerified && (
                <div className="absolute top-[20px] right-0 inline-flex items-center justify-center">
                    {otp.map((digit, index) => (
                        <React.Fragment key={index}>
                            <input
                                ref={(ref) => (inputRefs.current[index] = ref)}
                                regex="/[0-9]{1}$/"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(e, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                onPaste={handlePaste}
                                className="w-4 text-[16px] text-center focus:outline-none placeholder-gray-400 placeholder:font-bold"
                                placeholder="-"
                                type="text"
                                style={{ height: "16px" }}
                            />
                        </React.Fragment>
                    ))}
                    {isOTPValid && !showVerified && (
                        <div onClick={loading ? null : () => handleVerifyPhoneNumber(true)} className='ml-3'>
                            {loading ? <Spinner /> : <RightArrow className="cursor-pointer" />}
                        </div>
                    )}
                </div>
            )}
            {verifyPhoneNumber && showVerified && (
                <div className='absolute top-[22px] right-0'>
                    <VerifyIcon className="w-[20px] h-[20px]" />
                </div>
            )}
        </div>
    );
});

export default PhoneNumberInput;
